// A transient state that must be SEEN (2026-09-15 parity audit, busy-my-web#494 / #498).
//
// "Archiving 733 emails...", "HMRC groups is connecting to the ongoing.....", "Creating ongoing,
// Health issues" — the frames draw each of these as a moment of its own, and the audit could not
// capture a single one: the local BFF answers in under 150ms, so the band was replaced by the
// receipt before a frame was painted. A state that flashes for one paint is a state the user was
// never shown, and a receipt that appears with no "doing" before it reads as nothing having happened.
//
// So every commit that has a running band holds that band for at least this long. The call itself
// is not slowed — it runs at once, and only the MOMENT the receipt replaces the band is deferred to
// the later of "the call landed" and "the beat has passed". A failure is not held back at all: an
// error is not a state to savour.
//
// `wait` is injectable so a test can own the clock without faking timers.

/** How long a running band stays on screen at minimum — perceivable, and no longer. */
export const MINIMUM_BEAT_MS = 600

const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms))

/**
 * Resolve with the promise's value no earlier than `ms` after this was called; reject as soon as
 * the promise does.
 *
 * @template T
 * @param {Promise<T>|T} work   the call whose outcome is being waited for
 * @param {number} ms           the beat; anything ≤ 0 makes this a plain await
 * @param {(ms:number)=>Promise<void>} wait  the clock (tests hand in their own)
 * @returns {Promise<T>}
 */
export async function withMinimumBeat(work, ms = MINIMUM_BEAT_MS, wait = sleep) {
  const beat = ms > 0 ? wait(ms) : Promise.resolve()
  const result = await work
  await beat
  return result
}

export default withMinimumBeat
